import React, { useEffect, useRef } from 'react';

const Experience = () => {
  const experienceRef = useRef(null);
  
  useEffect(() => {
    const currentRef = experienceRef.current;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const experienceCards = entry.target.querySelectorAll('.experience-card');
          experienceCards.forEach((card, index) => {
            setTimeout(() => {
              card.classList.add('animate-fadeInUp');
            }, index * 200);
          });
          
          const timelineDots = entry.target.querySelectorAll('.timeline-dot');
          timelineDots.forEach((dot, index) => {
            setTimeout(() => {
              dot.classList.add('animate-scaleIn');
            }, index * 200);
          });
        }
      });
    }, { threshold: 0.1 });
    
    if (currentRef) {
      observer.observe(currentRef);
    }
    
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const experiences = [
    {
      company: "Wells Fargo",
      location: "Phoenix, AZ",
      role: "Sr.Software Engineer",
      period: "July 2024 - Present",
      achievements: [
        "Designed and developed microservices using Java 17 and Spring Boot to streamline loan application and management workflows",
        "Optimized loan status retrieval by 40% during peak loads by implementing Redis caching with PostgreSQL",
        "Containerized microservices with Docker and deployed on Kubernetes clusters to auto-scale for millions of concurrent requests",
        "Automated infrastructure provisioning with Terraform and AWS, reducing manual setup and deployment errors",
        "Enhanced system observability by configuring Prometheus and Grafana dashboards for API performance and resource monitoring"
      ]
    },
    {
      company: "Cigna HealthCare",
      location: "Bengalure, India",
      role: "Java developer",
      period: "Jan 2022 - July 2023",
      achievements: [
        "Developed backend microservices using Java 11 and Spring Boot for patient appointments and claims, reducing API response times by 30%",
        "Secured patient health records by implementing Spring Security with JWT and OAuth2, ensuring HIPAA compliance",
        "Deployed microservices using Docker and Kubernetes on AWS, enabling auto-scaling for 50K+ concurrent users",
        "Implemented Kafka messaging for real-time prescription and billing updates, improving inter-service communication",
        "Centralized logging with the ELK Stack and monitoring with Prometheus, reducing downtime incidents by 20%"
      ]
    },
    {
      company: "Walmart",
      location: "Bengalure, India",
      role: "Software Engineer",
      period: "Aug 2020 - Dec 2021",
      achievements: [
        "Designed and developed Order, Payment, and Notification microservices using Java 8 and Spring Boot",
        "Implemented Kafka-based event-driven communication, reducing order processing delays during peak sales events",
        "Optimized database queries and leveraged Redis caching to reduce order lookup latency by 40%",
        "Containerized microservices using Docker and managed deployments on Kubernetes for high availability",
        "Automated CI/CD pipelines with Jenkins, enabling continuous integration and smooth production releases"
      ]
    }
  ];

  return (
    <section id="experience" ref={experienceRef} className="py-20 bg-white/95 dark:bg-gray-900/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 opacity-1 transform translate-y-10">
          <h2 className="text-3xl md:text-4xl font-bold text-black dark:text-white">Work Experience</h2>
          <p className="mt-4 text-xl text-black dark:text-gray-400 max-w-3xl mx-auto font-bold">
            Proven track record of delivering impactful, scalable solutions at industry-leading companies.
          </p>
        </div>
        
        <div className="relative">
          <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700 transform md:-translate-x-1/2"></div>
          
          {experiences.map((exp, index) => (
            <div 
              key={index} 
              className={`relative mb-12 flex flex-col md:flex-row ${index % 2 === 0 ? 'md:flex-row-reverse' : ''} experience-card opacity-0 transform translate-y-10`}
            >
              <div className="md:w-1/2 p-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-bold text-black dark:text-white">{exp.role}</h3>
                      <div className="flex items-center mt-2">
                        <span className="font-bold text-indigo-600 dark:text-indigo-400">{exp.company}</span>
                        <span className="mx-2 text-black">•</span>
                        <span className="text-black dark:text-gray-400 font-bold">{exp.location}</span>
                      </div>
                    </div>
                    <span className="bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 px-3 py-1 rounded-full text-sm font-bold">
                      {exp.period}
                    </span>
                  </div>
                  
                  <ul className="mt-4 space-y-2">
                    {exp.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start text-black dark:text-gray-300 font-bold">
                        <span className="text-indigo-500 mt-1 mr-2">✓</span>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="absolute left-0 md:left-1/2 top-6 w-4 h-4 bg-indigo-600 rounded-full border-4 border-white dark:border-gray-900 transform md:-translate-x-1/2 timeline-dot scale-0"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;